import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
    LayoutDashboard, LifeBuoy, Handshake, ShieldAlert, MessageSquareQuote,
    BookOpen, Users, LogOut, Menu, X, HeartHandshake, ClipboardList, Inbox, UserPlus, BadgeCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const MANAGER = ["hr_leadership", "admin"];

const NAV = [
    { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: "all" },
    { to: "/app/support", label: "Support Cases", icon: LifeBuoy, roles: "all" },
    { to: "/app/mediation", label: "Mediation", icon: Handshake, roles: "manager" },
    { to: "/app/survey", label: "Culture Survey", icon: ClipboardList, roles: "all" },
    { to: "/app/report", label: "Report Assault", icon: ShieldAlert, roles: "all", danger: true },
    { to: "/app/stories", label: "People Stories", icon: MessageSquareQuote, roles: "all" },
    { to: "/app/policies", label: "Policy Library", icon: BookOpen, roles: "all" },
    { to: "/app/directory", label: "Directory", icon: Users, roles: "all" },
    { to: "/app/enquiries", label: "Enquiries", icon: Inbox, roles: "manager" },
    { to: "/app/waitlist", label: "Pre-registrations", icon: UserPlus, roles: "manager" },
    { to: "/app/partners", label: "Partners", icon: Handshake, roles: "manager" },
    { to: "/app/members", label: "Members", icon: BadgeCheck, roles: "manager" },
];

function roleLabel(r) {
    return { staff: "Staff", hr_leadership: "HR / Leadership", admin: "Administrator" }[r] || r;
}

export default function Layout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const isManager = MANAGER.includes(user?.role);
    const items = NAV.filter((n) => n.roles === "all" || (n.roles === "manager" && isManager));

    const handleLogout = async () => { await logout(); navigate("/login"); };

    return (
        <div className="min-h-screen flex bg-background">
            {/* Sidebar */}
            <aside className={`fixed lg:static z-40 h-full w-72 bg-nhs-ink text-white flex flex-col transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
                <div className="px-6 h-16 flex items-center gap-2 border-b border-white/10">
                    <HeartHandshake className="h-6 w-6 text-nhs-teal" />
                    <span className="font-heading font-extrabold text-lg tracking-tight">EquitySupport</span>
                </div>
                <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                    {items.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            onClick={() => setOpen(false)}
                            data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                                    isActive
                                        ? (n.danger ? "bg-nhs-red text-white" : "bg-nhs-blue text-white")
                                        : "text-slate-300 hover:bg-white/10 hover:text-white"
                                }`
                            }
                        >
                            <n.icon className="h-4 w-4 shrink-0" />
                            {n.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-4 border-t border-white/10">
                    <div className="mb-3">
                        <p className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</p>
                        <p className="text-xs text-slate-400">{roleLabel(user?.role)}</p>
                    </div>
                    <Button variant="secondary" className="w-full justify-start gap-2 bg-white/10 hover:bg-white/20 text-white border-0" onClick={handleLogout} data-testid="logout-button">
                        <LogOut className="h-4 w-4" /> Sign out
                    </Button>
                </div>
            </aside>

            {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

            {/* Main */}
            <div className="flex-1 flex flex-col min-w-0">
                <header className="sticky top-0 z-20 h-16 bg-white/90 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-4 sm:px-6">
                    <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
                        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                    <div className="hidden lg:block">
                        <p className="text-sm text-muted-foreground">{user?.organisation || "NHS Trust"}</p>
                    </div>
                    <Link to="/app/report">
                        <Button className="bg-nhs-red hover:bg-[#B51E14] text-white gap-2 rounded-md" data-testid="header-report-button">
                            <ShieldAlert className="h-4 w-4" /> Report Assault
                        </Button>
                    </Link>
                </header>
                <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-7xl w-full mx-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
